import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map, take } from 'rxjs';
import { Auth, authState, type User } from './auth';

type AuthGuardResult = boolean | string | unknown[];
export type AuthPipe = (
  user$: Observable<User | null>
) => Observable<AuthGuardResult>;
export type AuthPipeGenerator = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => AuthPipe;

/** Lets signed-out users through; sends signed-in users to `redirect`. */
export const redirectLoggedInTo =
  (redirect: string | unknown[]): AuthPipe =>
  user$ =>
    user$.pipe(map(user => (user ? redirect : true)));

/** Lets signed-in users through; sends signed-out users to `redirect`. */
export const redirectUnauthorizedTo =
  (redirect: string | unknown[]): AuthPipe =>
  user$ =>
    user$.pipe(map(user => (user ? true : redirect)));

/** Route config fragment: `{ path, component, ...canActivate(generator) }`. */
export function canActivate(pipeGenerator: AuthPipeGenerator): {
  canActivate: CanActivateFn[];
} {
  const guard: CanActivateFn = (next, state) => {
    const router = inject(Router);
    return pipeGenerator(
      next,
      state
    )(authState(inject(Auth)).pipe(take(1))).pipe(
      map((result): boolean | UrlTree =>
        typeof result === 'boolean'
          ? result
          : Array.isArray(result)
            ? router.createUrlTree(result)
            : router.parseUrl(result)
      )
    );
  };
  return { canActivate: [guard] };
}
